import React, { useEffect, useState } from 'react';
import { graphBuilderApi } from '../../services/api';
import './Explainability.css';

/**
 * IntegrityBadge
 *
 * Verifies the tamper-evident hash chain for an evaluation trace by
 * calling GET /api/explain/integrity/{traceId} on mount. Renders a
 * small badge: green check when verified, amber warning when not, or
 * a muted pill when the trace_id is unknown.
 *
 * Props:
 *  - traceId:  the InsightId or trace_id used as the chain key
 */
const IntegrityBadge = ({ traceId }) => {
  const [status, setStatus] = useState('loading'); // loading|verified|failed|unknown|error
  const [message, setMessage] = useState('');
  const [totalEvents, setTotalEvents] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!traceId) {
      setStatus('unknown');
      return;
    }

    setStatus('loading');
    graphBuilderApi
      .verifyIntegrity(traceId)
      .then((data) => {
        if (cancelled) return;
        setStatus(data?.verified ? 'verified' : 'failed');
        setMessage(data?.message || '');
        setTotalEvents(data?.total_events || 0);
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
        setMessage('Could not verify evaluation integrity right now.');
      });

    return () => { cancelled = true; };
  }, [traceId]);

  const cls = `explain-integrity-badge explain-integrity-badge--${status}`;
  const label = (() => {
    switch (status) {
      case 'verified':
        return totalEvents
          ? `Integrity verified (${totalEvents} events)`
          : 'Integrity verified';
      case 'failed':   return 'Integrity check failed';
      case 'loading':  return 'Verifying…';
      case 'unknown':  return 'Integrity check unavailable';
      case 'error':
      default:         return 'Integrity check error';
    }
  })();

  const tooltip = message || label;

  return (
    <span className={cls} title={tooltip} aria-live="polite">
      <span className="explain-integrity-dot" />
      {label}
    </span>
  );
};

export default IntegrityBadge;
